// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hearsay — the eight pieces of testimony, and the decks built from them.
//
// A card's RANK is three things at once, and that overloading is the whole design: it is what the
// card DOES, what it is WORTH at the close, and what a rival has to name to put you out. Nothing
// here needs a colour to be read — every card prints its rank as a digit and its name in full — so
// the game survives greyscale and a bad phone screen. See src/palette.ts.
//
// The deck composition is PUBLIC and fixed. That is the entire basis of the deduction: subtract what
// is on the table from what you know is in the box, and the remainder is what the room is holding.

export interface CardDef {
  rank: number;
  /** Printed on the card face and in the docket. */
  name: string;
  /** The rule, in the words the player is shown. */
  power: string;
  /** One line of colour for the card detail sheet. */
  flavour: string;
}

/**
 * Ranks 1..8. The curve — five of the lowest, one each of the top three — is what makes naming a
 * rank a real decision rather than a guess: the commonest card is the one you may not name.
 */
export const CARDS: readonly CardDef[] = [
  {
    rank: 1,
    name: 'Bailiff',
    power:
      'Name a rival and a rank other than Bailiff. If that is their testimony, they are put out. ' +
      'If it is not, only you are told whether their testimony is higher or lower.',
    flavour: 'He has cleared this room a hundred times and never once looked pleased about it.',
  },
  {
    rank: 2,
    name: 'Clerk',
    power: 'Read one rival’s testimony in secret. Only you see it.',
    flavour: 'Everything passes across her desk, and she remembers all of it.',
  },
  {
    rank: 3,
    name: 'Witness',
    power: 'Compare testimony privately with a rival. The lower rank is dismissed. Equal, and nothing happens.',
    flavour: 'Two accounts of the same afternoon. Only one of them can stand.',
  },
  {
    rank: 4,
    name: 'Recusal',
    power: 'You step off the record. Until your next turn you cannot be named or targeted.',
    flavour: 'A conflict of interest, declared just late enough to be useful.',
  },
  {
    rank: 5,
    name: 'Retrial',
    power: 'A rival — or you — discards their testimony and draws again. Discarding the Verdict this way puts them out.',
    flavour: 'Start the whole thing over and hope the second telling goes better.',
  },
  {
    rank: 6,
    name: 'Advocate',
    power: 'Trade testimony with a rival, sight unseen.',
    flavour: 'He will argue either side. Today he is arguing both.',
  },
  {
    rank: 7,
    name: 'The Judge',
    power: 'No power. But if you also hold the Advocate or a Retrial, you must discard the Judge instead of them.',
    flavour: 'She will not sit while the record is being rewritten around her.',
  },
  {
    rank: 8,
    name: 'The Verdict',
    power: 'No power. Discard it for any reason at all and you are out of the hearing.',
    flavour: 'Yours to hold. Never yours to hand over.',
  },
] as const;

export const BAILIFF = 1;
export const CLERK = 2;
export const WITNESS = 3;
export const RECUSAL = 4;
export const RETRIAL = 5;
export const ADVOCATE = 6;
export const JUDGE = 7;
export const VERDICT = 8;

/** The ranks the Judge refuses to sit beside. Pinned independently by tests/mechanism.test.ts. */
export const JUDGE_FORCES: readonly number[] = [RETRIAL, ADVOCATE];

/**
 * THE ANTI-LUCK SPINE, and the one place this deviates from the shape it descends from.
 *
 * Five of sixteen cards are the Bailiff. In the original, naming a rank and missing is a wasted turn
 * and a coin flip — a third of the deck is a lottery ticket. Here a miss still pays: the caller, and
 * ONLY the caller, is told whether the testimony is higher or lower than the rank they named. A hand
 * full of Bailiffs stops being a lottery and becomes a binary search.
 *
 * The asymmetry is the whole point and it was nearly got wrong. Announcing higher/lower to the TABLE
 * makes the read a public good: the caller spends the whole turn and every rival gets the same
 * information for free, so the card differentiates nobody and gets worse the more players there are.
 * Sending it only to the caller means the turn buys a private halving of one rival's possibility
 * space, and the room learns exactly the crumb it would have seen anyway — that a call was made,
 * against whom, naming what, and that it missed.
 */
export const MISS_HINT_IS_PRIVATE = true;

export const cardOf = (rank: number): CardDef => CARDS[rank - 1] ?? CARDS[0];
export const nameOf = (rank: number): string => cardOf(rank).name;

/** A deck is a count per rank, indexed 1..8. Index 0 is unused and always zero. */
export type DeckSpec = readonly number[];

/**
 * The three decks.
 *
 * `SMALL` is the reference sixteen. `WIDE` scales it for five and six seats by adding to the LOW and
 * MIDDLE ranks only — the top three stay unique, because "there is exactly one Verdict in the room"
 * is the fact every deduction in the game hangs off, and duplicating it would quietly delete the
 * game's sharpest moment.
 */
export const SMALL: DeckSpec = [0, 5, 2, 2, 2, 2, 1, 1, 1]; // 16
export const WIDE: DeckSpec = [0, 7, 3, 3, 3, 3, 1, 1, 1]; // 22
export const BRIEF: DeckSpec = [0, 8, 3, 3, 3, 3, 2, 1, 1]; // 24

export const deckSize = (d: DeckSpec): number => d.reduce((a, b) => a + b, 0);

/** The deck as a flat list of ranks, in composition order. Shuffling is the caller's job. */
export function deckCards(d: DeckSpec): number[] {
  const out: number[] = [];
  for (let rank = 1; rank < d.length; rank++) for (let k = 0; k < d[rank]; k++) out.push(rank);
  return out;
}
